import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStats, getSubmissions, getSubmissionDetails, deleteSubmission, logout, getDownloadUrl } from '../api';
import Layout from './Layout';
import DashboardCard from './DashboardCard';
import CircularProgress from './CircularProgress';
import DataTable from './DataTable';
import DashboardSkeleton from './skeletons/DashboardSkeleton';
import FileViewerModal from './FileViewerModal';
import { formatDateEST, getCurrentDateEST } from '../utils/dateUtils';
import './AdminDashboard.css';

// Form type labels with icons and colors
const getFormTypeLabel = (formType) => {
  const formTypes = {
    'Concrete Safety Daily Log': { label: 'Concrete Safety', icon: '🏗️', color: '#7c3aed' },
    'Facade Daily Log': { label: 'Facade Daily Log', icon: '🏢', color: '#db2777' },
    'Fire Safety Manager Daily Log': { label: 'Fire Safety Manager', icon: '🔥', color: '#dc2626' },
    'Site Safety Coordinator Daily Log': { label: 'Site Safety Coordinator', icon: '🏗️', color: '#0891b2' },
    'Site Safety Manager Daily Log': { label: 'Site Safety Manager', icon: '🏗️', color: '#059669' },
    'Superintendent Daily Log': { label: 'Superintendent Log', icon: '📋', color: '#9333ea' },
    'Weekly TimeSheet': { label: 'Weekly Timesheet', icon: '⏰', color: '#ea580c' },
    'Other': { label: 'Other', icon: '📄', color: '#6b7280' }
  };

  return formTypes[formType] || { label: formType || 'Daily Log', icon: '📄', color: '#3b82f6' };
};

function AdminDashboard({ user, onLogout }) {
  const [stats, setStats] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [viewingPDFInModal, setViewingPDFInModal] = useState(null); // {id, name, url} - PDF viewer inside modal
  const [viewingPDFStandalone, setViewingPDFStandalone] = useState(null); // {id, name, url} - PDF viewer standalone modal

  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');

    // Get today's date in YYYY-MM-DD format (EST timezone)
    const today = getCurrentDateEST();

    const todayFilters = {
      from_date: today,
      to_date: today,
      page: 1
    };

    try {
      // Load stats and today's submissions in parallel for better performance
      const [statsResponse, submissionsResponse] = await Promise.all([
        getStats().catch(err => ({ success: false, error: err.message })),
        getSubmissions(todayFilters).catch(err => ({ success: false, error: err.message }))
      ]);

      // Handle stats
      if (statsResponse.success) {
        setStats(statsResponse.stats);
      }

      // Handle submissions
      if (submissionsResponse.success) {
        setSubmissions(submissionsResponse.data);
        setPagination(submissionsResponse.pagination);
      } else {
        setError(submissionsResponse.error || 'Failed to load submissions');
      }

      // Check for unauthorized
      if (statsResponse.error?.includes('Unauthorized') || submissionsResponse.error?.includes('Unauthorized')) {
        handleLogout();
      }
    } catch (error) {
      setError('An unexpected error occurred');
      console.error('Load data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await getStats();
      if (response.success) {
        setStats(response.stats);
      }
    } catch (error) {
      if (error.message.includes('Unauthorized')) {
        handleLogout();
      }
    }
  };

  const handleViewDetails = async (id) => {
    setLoadingDetails(true);
    setShowModal(true); // Show modal immediately with loading state
    setSelectedSubmission(null); // Clear previous data

    try {
      const response = await getSubmissionDetails(id);
      if (response.success) {
        setSelectedSubmission(response.submission);
      } else {
        alert('Failed to load submission details');
        setShowModal(false);
      }
    } catch (error) {
      alert('Failed to load submission details');
      setShowModal(false);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this submission? This cannot be undone.')) {
      try {
        const response = await deleteSubmission(id);
        if (response.success) {
          alert('Submission deleted successfully');
          loadData();
          if (showModal) {
            setShowModal(false);
          }
        }
      } catch (error) {
        alert('Failed to delete submission: ' + error.message);
      }
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }

    localStorage.removeItem('admin_user');
    if (onLogout) {
      onLogout();
    }
    navigate('/admin');
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedSubmission(null);
    setViewingPDFInModal(null);
  };

  const closePDFViewer = () => {
    setViewingPDFStandalone(null);
  };

  const handleViewPDF = async (id) => {
    try {
      const response = await getSubmissionDetails(id);
      if (response.success && response.submission.files.length > 0) {
        // Prepare all files with download URLs
        const filesWithUrls = response.submission.files.map(file => ({
          ...file,
          download_url: getDownloadUrl(file.id, true),
          size_formatted: formatFileSize(file.file_size)
        }));

        // Open FileViewerModal with all files
        setViewingPDFStandalone({
          files: filesWithUrls,
          submission: response.submission
        });
      } else {
        alert('No files found in this submission');
      }
    } catch (error) {
      alert('Failed to load PDF: ' + error.message);
    }
  };

  const formatFileSize = (bytes) => {
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return '-';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  if (!user) {
    navigate('/admin');
    return null;
  }

  // Calculate completion percentage for demonstration
  const completionPercentage = stats ? Math.min(Math.round((stats.today_submissions / 12) * 100), 100) : 0;
  const profileStrength = stats ? Math.min(Math.round((stats.active_workers / 50) * 100), 100) : 0;

  return (
    <Layout user={user} onLogout={handleLogout}>
      <div className="admin-dashboard">
        <div className="page-header">
          <div>
            <h1 className="page-title">Dashboard</h1>
            <p className="page-subtitle">Overview of today's work documents and submissions</p>
          </div>
          <button
            onClick={() => navigate('/admin/all-submissions')}
            className="modern-btn primary view-all-btn"
            style={{ height: 'fit-content' }}
          >
            <span className="btn-text-full">View All Submissions</span>
            <span className="btn-text-short">View All</span>
          </button>
        </div>

        {/* Stats Grid */}
        {stats && (
          <div className="dashboard-grid">
            <DashboardCard
              title="Work Documents"
              value={stats.total_submissions}
              subtitle="Expire Soon"
              darkMode={true}
              arrow={true}
              onClick={() => navigate('/admin/submissions')}
            />

            <DashboardCard
              title="Today's Submissions"
              value={stats.today_submissions}
              subtitle={`${completionPercentage}% of daily target`}
            >
              <CircularProgress
                percentage={completionPercentage}
                size={120}
                strokeWidth={10}
                color="#00d4aa"
                label="Target"
              />
            </DashboardCard>

            <DashboardCard
              title="This Week"
              value={stats.week_submissions}
              subtitle="Total this week"
              trend={{ type: 'up', text: '+12% from last week' }}
            />

            <DashboardCard
              title="Active Workers"
              value={stats.active_workers}
              subtitle={`Profile strength: ${profileStrength}%`}
            >
              <CircularProgress
                percentage={profileStrength}
                size={100}
                strokeWidth={8}
                color="#00d4aa"
              />
            </DashboardCard>
          </div>
        )}

        {error && (
          <div className="error-banner">
            <span>⚠</span> {error}
          </div>
        )}

        {loading ? (
          <DashboardSkeleton />
        ) : (
          <>
            <DataTable
              title="Today's Work Documents"
              searchable={false}
              columns={[
                {
                  key: 'submission_date',
                  label: 'Date',
                  sortable: true,
                  render: (value) => formatDateEST(value)
                },
                {
                  key: 'worker_name',
                  label: 'Worker Name',
                  sortable: true
                },
                {
                  key: 'job_site',
                  label: 'Job Site',
                  sortable: true
                },
                {
                  key: 'comments',
                  label: 'Comments',
                  render: (value) => truncateText(value)
                },
                {
                  key: 'file_count',
                  label: 'Files',
                  render: (value) => (
                    <span className="status-badge success">{value}</span>
                  )
                },
                {
                  key: 'actions',
                  label: 'Actions',
                  render: (_, row) => (
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewPDF(row.id);
                        }}
                        className="table-action-btn primary"
                        title="View PDF"
                      >
                        👁️ View
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(row.id);
                        }}
                        className="table-action-btn danger"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  )
                }
              ]}
              data={submissions}
            />
          </>
        )}

        {/* Modern Modal */}
        {showModal && (
          <div className="modern-modal-overlay" onClick={closeModal}>
            <div
              className="modern-modal"
              onClick={(e) => e.stopPropagation()}
              style={viewingPDFInModal ? { maxWidth: '95%', width: '1400px', height: '90vh' } : {}}
            >
              <div className="modern-modal-header">
                <h2>Submission Details</h2>
                <button onClick={closeModal} className="modal-close-btn">
                  ✕
                </button>
              </div>

              {loadingDetails ? (
                <div className="modern-modal-body" style={{ textAlign: 'center', padding: '60px 20px' }}>
                  <div className="loading-spinner" style={{
                    border: '4px solid #f3f3f3',
                    borderTop: '4px solid #6366f1',
                    borderRadius: '50%',
                    width: '50px',
                    height: '50px',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 20px'
                  }}></div>
                  <p style={{ color: '#666', fontSize: '16px' }}>Loading submission details...</p>
                </div>
              ) : selectedSubmission ? (
              <div
                className="modern-modal-body"
                style={viewingPDFInModal ? {
                  maxHeight: 'calc(90vh - 180px)',
                  overflowY: 'auto'
                } : {}}
              >
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Worker Name</label>
                    <p>{selectedSubmission.worker_name}</p>
                  </div>
                  <div className="detail-item">
                    <label>Job Site</label>
                    <p>{selectedSubmission.job_site}</p>
                  </div>
                  <div className="detail-item">
                    <label>Date</label>
                    <p>{formatDateEST(selectedSubmission.submission_date)}</p>
                  </div>
                  <div className="detail-item">
                    <label>Form Type</label>
                    <p>
                      {(() => {
                        const formInfo = getFormTypeLabel(selectedSubmission.form_type);
                        return (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: 600,
                            backgroundColor: `${formInfo.color}15`,
                            color: formInfo.color,
                            border: `1px solid ${formInfo.color}40`
                          }}>
                            <span>{formInfo.icon}</span>
                            <span>{formInfo.label}</span>
                          </span>
                        );
                      })()}
                    </p>
                  </div>
                </div>

                <div className="detail-item full-width">
                  <label>Comments</label>
                  <p>{selectedSubmission.comments || 'No comments'}</p>
                </div>

                <div className="files-section-modern">
                  <h3>Attached Files ({selectedSubmission.files.length})</h3>
                  {selectedSubmission.files.length > 0 ? (
                    <div className="files-grid">
                      {selectedSubmission.files.map((file) => {
                        const ext = file.file_name.split('.').pop().toLowerCase();
                        const isPDF = ext === 'pdf';

                        return (
                          <div key={file.id} className="file-card">
                            <div className="file-card-icon">
                              {isPDF ? '📕' : '📄'}
                            </div>
                            <div className="file-card-info">
                              <div className="file-card-name">{file.file_name}</div>
                              <div className="file-card-size">{formatFileSize(file.file_size)}</div>
                            </div>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              {isPDF && (
                                <button
                                  onClick={() => setViewingPDFInModal({
                                    id: file.id,
                                    name: file.file_name,
                                    url: getDownloadUrl(file.id, true)
                                  })}
                                  className="file-download-btn"
                                  style={{ background: '#0891b2', cursor: 'pointer', border: 'none' }}
                                  title="View PDF"
                                >
                                  👁
                                </button>
                              )}
                              <a
                                href={getDownloadUrl(file.id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="file-download-btn"
                                title="Download file"
                              >
                                ↓
                              </a>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="no-files">No files attached</p>
                  )}
                </div>

                {/* PDF Viewer Section */}
                {viewingPDFInModal && (
                  <div style={{ marginTop: '20px' }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '12px',
                      paddingBottom: '12px',
                      borderBottom: '2px solid #e5e7eb'
                    }}>
                      <h3 style={{ margin: 0, fontSize: '18px', color: '#1f2937' }}>
                        📄 {viewingPDFInModal.name}
                      </h3>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => {
                            const pdfContainer = document.getElementById('pdf-viewer-container');
                            if (pdfContainer) {
                              if (document.fullscreenElement) {
                                document.exitFullscreen();
                              } else {
                                pdfContainer.requestFullscreen();
                              }
                            }
                          }}
                          className="modern-btn"
                          style={{
                            padding: '6px 12px',
                            fontSize: '13px',
                            background: '#10b981',
                            color: '#fff'
                          }}
                          title="View Fullscreen"
                        >
                          ⛶ Fullscreen
                        </button>
                        <a
                          href={viewingPDFInModal.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="modern-btn"
                          style={{
                            textDecoration: 'none',
                            padding: '6px 12px',
                            fontSize: '13px',
                            background: '#6366f1',
                            color: '#fff'
                          }}
                        >
                          ↓ Download
                        </a>
                        <button
                          onClick={() => setViewingPDFInModal(null)}
                          className="modern-btn secondary"
                          style={{ padding: '6px 12px', fontSize: '13px' }}
                        >
                          ✕ Close PDF
                        </button>
                      </div>
                    </div>
                    <div
                      id="pdf-viewer-container"
                      style={{
                        width: '100%',
                        height: '600px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        position: 'relative',
                        background: '#fff'
                      }}
                    >
                      {/* Exit Fullscreen Button - Shows only in fullscreen */}
                      <button
                        onClick={() => {
                          if (document.fullscreenElement) {
                            document.exitFullscreen();
                          }
                        }}
                        style={{
                          position: 'absolute',
                          top: '10px',
                          right: '10px',
                          zIndex: 1000,
                          padding: '10px 16px',
                          background: 'rgba(0, 0, 0, 0.8)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '600',
                          display: 'none',
                          alignItems: 'center',
                          gap: '6px',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                          transition: 'background 0.2s ease'
                        }}
                        className="fullscreen-exit-btn"
                        onMouseEnter={(e) => e.target.style.background = 'rgba(239, 68, 68, 0.9)'}
                        onMouseLeave={(e) => e.target.style.background = 'rgba(0, 0, 0, 0.8)'}
                      >
                        ✕ Exit Fullscreen
                      </button>
                      <iframe
                        src={viewingPDFInModal.url}
                        style={{
                          width: '100%',
                          height: '100%',
                          border: 'none'
                        }}
                        title={viewingPDFInModal.name}
                      />
                    </div>
                  </div>
                )}
              </div>
              ) : null}

              {!loadingDetails && selectedSubmission && (
              <div className="modern-modal-footer">
                <button onClick={closeModal} className="modern-btn secondary">
                  Close
                </button>
                <button
                  onClick={() => handleDelete(selectedSubmission.id)}
                  className="modern-btn danger"
                >
                  Delete Submission
                </button>
              </div>
              )}
            </div>
          </div>
        )}

        {/* File Viewer Modal - Smart viewer for all file types */}
        {viewingPDFStandalone && viewingPDFStandalone.files && (
          <FileViewerModal
            files={viewingPDFStandalone.files}
            initialIndex={0}
            onClose={closePDFViewer}
          />
        )}
      </div>
    </Layout>
  );
}

export default AdminDashboard;
